/**
 * free-gift-auto-remove.js
 *
 * Removes a free gift from the cart whenever its required "main" product is no
 * longer in the cart. Driven by rules defined in the `free_gift_rule`
 * metaobject and rendered into #free-gift-rules by snippets/free-gift-rules.liquid.
 *
 * Why: the store offers gifts (e.g. Reusable Hot Pack) that are only valid as
 * part of a main-product offer. The pricing discount is handled by an app; this
 * script enforces the "no orphaned free gift" rule on the storefront so a
 * customer can't proceed with only the gift in their cart.
 *
 * Integration: hooks into Dawn's pub/sub cart system (PUB_SUB_EVENTS.cartUpdate)
 * rather than patching cart.js. After removing an orphaned gift we re-publish a
 * cartUpdate from our own source, which makes cart.js / cart-drawer.js re-render
 * the cart and drawer from the server.
 */
(function () {
  'use strict';

  const SOURCE = 'free-gift-auto-remove';

  const rulesEl = document.getElementById('free-gift-rules');
  if (!rulesEl) return;

  let parsed = [];
  try {
    parsed = JSON.parse(rulesEl.textContent.trim() || '[]');
  } catch (e) {
    console.warn('[free-gift] Could not parse #free-gift-rules JSON:', e);
    return;
  }

  // Map each gift product id -> set of main product ids that justify keeping it.
  // A gift is removed only when NONE of its mains are present (handles a gift
  // shared across multiple offers).
  const giftToMains = new Map();
  parsed.forEach((rule) => {
    const gift = Number(rule.gift);
    const main = Number(rule.main);
    if (!gift || !main) return;
    if (!giftToMains.has(gift)) giftToMains.set(gift, new Set());
    giftToMains.get(gift).add(main);
  });
  if (giftToMains.size === 0) return;

  let inFlight = null;

  function getCart() {
    return fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());
  }

  // Return the line-item keys of gift products whose main product is absent.
  function orphanedGiftKeys(cart) {
    const productIdsInCart = new Set(cart.items.map((item) => item.product_id));
    const keys = [];

    cart.items.forEach((item) => {
      const mains = giftToMains.get(item.product_id);
      if (!mains) return; // not a gift product we manage

      let mainPresent = false;
      mains.forEach((mainId) => {
        if (productIdsInCart.has(mainId)) mainPresent = true;
      });

      if (!mainPresent) keys.push(item.key);
    });

    return keys;
  }

  // Section IDs to have Shopify re-render in the change.js response, based on
  // what is actually present on the current page.
  function sectionsToRequest() {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) ids.push(mainItems.dataset.id);
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) ids.push(mainFooter.dataset.id);
    return [...new Set(ids)];
  }

  function changeLine(key, quantity, sections) {
    const body = JSON.stringify({
      id: key,
      quantity,
      sections,
      sections_url: window.location.pathname,
    });
    const config =
      typeof fetchConfig === 'function'
        ? { ...fetchConfig(), body }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
          };
    return fetch(`${window.routes.cart_change_url}`, config).then((response) => response.json());
  }

  function replaceInnerFromSection(sectionHTML, sourceSelector, targetEl) {
    if (!sectionHTML || !targetEl) return;
    const source = new DOMParser()
      .parseFromString(sectionHTML, 'text/html')
      .querySelector(sourceSelector);
    if (source) targetEl.innerHTML = source.innerHTML;
  }

  // Re-render the cart UI exactly the way the theme does on a native removal,
  // including the empty-state transition that the pub/sub-only path misses
  // (replacing the full .drawer__inner and toggling the `is-empty` class).
  function renderCart(state) {
    if (!state || !state.sections) return;
    const isEmpty = state.item_count === 0;

    // Cart drawer: replace the entire inner (header + items + footer + empty state).
    const drawer = document.querySelector('cart-drawer');
    if (drawer && state.sections['cart-drawer']) {
      replaceInnerFromSection(
        state.sections['cart-drawer'],
        '.drawer__inner',
        drawer.querySelector('.drawer__inner')
      );
      drawer.classList.toggle('is-empty', isEmpty);
    }

    // Header cart-count bubble.
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && state.sections['cart-icon-bubble']) {
      replaceInnerFromSection(state.sections['cart-icon-bubble'], '.shopify-section', bubble);
    }

    // Main cart page (when the cart is a page, not a drawer).
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems) {
      replaceInnerFromSection(
        state.sections[mainItems.dataset.id],
        '.js-contents',
        mainItems.querySelector('.js-contents')
      );
      mainItems.classList.toggle('is-empty', isEmpty);
      const cartItems = document.querySelector('cart-items');
      if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    }
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter) {
      replaceInnerFromSection(
        state.sections[mainFooter.dataset.id],
        '.js-contents',
        mainFooter.querySelector('.js-contents')
      );
      mainFooter.classList.toggle('is-empty', isEmpty);
    }
  }

  // Returns a result object: { itemCount, changed, error }.
  //   itemCount → cart item count after enforcement (null if the check failed)
  //   changed   → whether any gift was removed
  //   error     → true if the check could not complete (network, etc.)
  function doEnforce() {
    const sections = sectionsToRequest();

    return getCart()
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) {
          return { itemCount: null, changed: false, error: true };
        }
        const keys = orphanedGiftKeys(cart);
        if (keys.length === 0) {
          return { itemCount: cart.item_count, changed: false, error: false };
        }

        // Remove sequentially. Shopify line-item keys are stable identifiers
        // (not positional indexes), so removing one does not invalidate the rest.
        // Request re-rendered sections so we can refresh the UI from the final state.
        return keys
          .reduce(
            (chain, key) => chain.then(() => changeLine(key, 0, sections)),
            Promise.resolve(null)
          )
          .then((latestState) => {
            // Refresh the cart UI ourselves (fixes the empty-drawer state), then
            // notify other cart listeners (upsells, shipping bars, price-per-item, etc.).
            if (latestState) {
              renderCart(latestState);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: latestState });
              }
            }
            const itemCount = latestState ? latestState.item_count : cart.item_count;
            return { itemCount, changed: true, error: false };
          });
      })
      .catch((e) => {
        console.warn('[free-gift] Enforcement failed:', e);
        return { itemCount: null, changed: false, error: true };
      });
  }

  // Serialize runs so a checkout click waits on any in-flight removal instead
  // of starting a competing one.
  function enforce() {
    if (inFlight) return inFlight;
    inFlight = doEnforce().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  // React to every cart change made through the theme's pub/sub system.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return; // ignore our own re-publish
      return enforce();
    });
  }

  // --- Checkout guard ---------------------------------------------------------
  // Closes the race where a customer removes the main product and clicks
  // checkout before the asynchronous gift removal has finished. We block the
  // native submit, verify/clean the cart server-side, then continue only if
  // valid items remain.
  let bypassCheckout = false;

  function proceedToCheckout() {
    const btn = document.querySelector('#CartDrawer-Checkout, #checkout, [name="checkout"]');
    // Submitting the live form preserves locale/market prefixes and cart note.
    if (btn && btn.form && typeof btn.form.requestSubmit === 'function') {
      btn.form.requestSubmit(btn); // fires 'submit', not 'click' — no re-intercept
      return;
    }
    if (btn) {
      bypassCheckout = true; // a synthetic click re-enters the handler; let it pass
      btn.click();
      return;
    }
    window.location.href = window.routes.cart_url.replace(/\/cart$/, '/checkout');
  }

  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('[name="checkout"]');
      if (!btn) return;
      if (bypassCheckout) {
        bypassCheckout = false;
        return;
      }

      // Hold the submit until the cart is verified clean.
      event.preventDefault();
      event.stopImmediatePropagation();

      enforce().then((result) => {
        if (!result || result.error) {
          // Could not verify (e.g. network) — don't trap the customer; the
          // server-side validation (if configured) remains the safety net.
          proceedToCheckout();
          return;
        }
        if (result.itemCount > 0) {
          proceedToCheckout();
        }
        // itemCount === 0 → the cart became empty after removing the orphaned
        // gift, so we stay put: the customer cannot check out the gift alone.
      });
    },
    true
  );

  // Catch a cart that is already in an invalid state when the page loads
  // (e.g. the main product was removed in another tab).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforce);
  } else {
    enforce();
  }
})();
